import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  styled,
  Tooltip,
  IconButton,
} from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { BarChart } from '@mui/x-charts/BarChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import axiosInstance from '../../lib/axios';
import { toast } from 'react-toastify';
import { ToastContainer } from 'react-toastify';
import CalendarPage from './CalendarPage';
import { useSelector } from 'react-redux';
import { selectGlobalProperty } from '../../redux/propertySlice';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { LinearProgress, Chip } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import AccountingTable from './AccountingTable';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { usePermissions } from '../../GlobalFunction/userPermissions';

// Styled components (unchanged)
const StyledCard = styled(Paper)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2),
  borderRadius: '12px',
  background: 'linear-gradient(135deg, #2c2c2c 0%, #1a1a1a 100%)',
  color: '#fff',
  boxShadow: '0 6px 20px rgba(0, 0, 0, 0.3)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 12px 30px rgba(0, 0, 0, 0.4)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
  },
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: '12px',
  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
  background: 'linear-gradient(135deg, #ffffff 0%, #f5f7fa 100%)',
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}));

const AccountingDash = () => {
  const UserId = sessionStorage.getItem('UserId');
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalCollection, setTotalCollection] = useState(0);
  const [totalDues, setTotalDues] = useState(0);
  const [totalCreditNote, setTotalCreditNote] = useState(0);
  const [quarterlyRevenueData, setQuarterlyRevenueData] = useState([]);
  const [mnthlyCollData, setMnthlyCollData] = useState([]);
  const [billunbilledData, setBillUnbillData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [receivedAmnt, setReceivedAmnt] = useState([]);
  const globalProperty = useSelector(selectGlobalProperty);
  // const currentYear = dayjs().year();
  // const previousYear = currentYear - 1;
  // const [dateFrom, setDateFrom] = useState(dayjs(`${previousYear}-04-01`));
  // const [dateTo, setDateTo] = useState(dayjs(`${currentYear}-04-01`).subtract(1, 'day'));
  const today = dayjs();
  let startYear, endYear;

  if (today.month() >= 3) {
    startYear = today.year();
    endYear = today.year() + 1;
  } else {
    startYear = today.year() - 1;
    endYear = today.year();
  }

  const [dateFrom, setDateFrom] = useState(dayjs(`${startYear}-04-01`));
  const [dateTo, setDateTo] = useState(dayjs(`${endYear}-03-31`));
  const navigate = useNavigate();

  const Permissions = usePermissions("Payment Advice");
  const CreditPermissions = usePermissions("Credit Notes");
  const reciptPermission = usePermissions("Receipt Voucher")

  const chartColors = ['#8884d8', '#82ca9d', '#daa43aff', '#ff7300'];

  const formatCurrency = (amount) => {
    const num = Number(amount);
    if (isNaN(num)) return '₹0';
    return `₹ ${num.toLocaleString('en-IN')}`;
  };

  useEffect(() => {
    fetchData();
    fetchQuarterlyRevenueReport();
    fetchMonthlyCollectionData();
    billedUnbilledData();
    categoryWiseRevenue();
    receivedAmntType();
  }, [globalProperty]);

  const handleFetchedData = async () => {
    await fetchData();
    await fetchQuarterlyRevenueReport();
    await fetchMonthlyCollectionData();
    await billedUnbilledData();
    await categoryWiseRevenue();
    await receivedAmntType();
  };

  const fetchData = async () => {
    const flags = ['TotalRevenue', 'TotalCollection', 'TotalDue', 'TotalCN'];
    try {
      const responses = await Promise.all(
        flags.map((flag) =>
          axiosInstance.post('AccDashboard/AccDashboard?currentPage=1&limit=15', {
            PropId: globalProperty || 2,
            FromDt: dateFrom.format('YYYY-MM-DD'),
            ToDt: dateTo.format('YYYY-MM-DD'),
            Flag: flag,
            start: 1,
            PageSize: 10,
            SearchText: "",
          })
        )
      );

      responses.forEach((response, index) => {
        if (response.data.Status === 0) {
          const data = response?.data?.Data;
          const total = Array.isArray(data) ? data[0]?.TotalCount : data?.TotalCount || 0;
          if (flags[index] === 'TotalRevenue') {
            setTotalRevenue(total);
          } else if (flags[index] === 'TotalCollection') {
            setTotalCollection(total);
          } else if (flags[index] === 'TotalDue') {
            setTotalDues(total);
          } else if (flags[index] === 'TotalCN') {
            setTotalCreditNote(total);
          }
        } else {
          if (flags[index] === 'TotalRevenue') {
            setTotalRevenue(0);
          } else if (flags[index] === 'TotalCollection') {
            setTotalCollection(0);
          } else if (flags[index] === 'TotalDue') {
            setTotalDues(0);
          } else if (flags[index] === 'TotalCN') {
            setTotalCreditNote(0);
          }
          // toast.info(`Failed to fetch ${flags[index]}`);
        }
      });
    } catch (error) {
      toast.error('Error fetching dashboard data. Please try again.');
    }
  };

  const fetchQuarterlyRevenueReport = async () => {
    try {
      const response = await axiosInstance.post('AccDashboard/AccDashboard?currentPage=1&limit=15', {
        PropId: globalProperty,
        FromDt: dateFrom.format('YYYY-MM-DD'),
        ToDt: dateTo.format('YYYY-MM-DD'),
        Flag: "QuarterRevenue",
        start: 1,
        PageSize: 10,
        SearchText: ""
      });

      if (response.data.Status === 0) {
        const data = response.data.Data;
        const formattedData = data.map((item, index) => ({
          id: index,
          value: item.TotalCount || 0,
          // label: `${item.MonthNm} ${item.YearNm}`,
          label: `Q${index + 1} - ₹${Number(item.TotalCount).toLocaleString('en-IN')}`,
          color: chartColors[index % chartColors.length],
        }));
        setQuarterlyRevenueData(formattedData);
      } else {
        setQuarterlyRevenueData([]);
        // toast.info('Failed to fetch quarterly revenue data');
      }
    } catch (error) {
      toast.error('Error fetching quarterly revenue data. Please try again.');
    }
  };

  const fetchMonthlyCollectionData = async () => {
    try {
      const response = await axiosInstance.post('AccDashboard/AccDashboard?currentPage=1&limit=15', {
        PropId: globalProperty,
        FromDt: dateFrom.format('YYYY-MM-DD'),
        ToDt: dateTo.format('YYYY-MM-DD'),
        Flag: "TotalCollectionMonth",
        start: 1,
        PageSize: 10,
        SearchText: ""
      });

      if (response.data.Status === 0) {
        const data = response.data.Data;
        const formattedData = data.map((item) => ({
          period: item.MonthNm || "",
          count: item.TotalCount || 0,
        }));
        setMnthlyCollData(formattedData);
      } else {
        setMnthlyCollData([]);
        // toast.info('Failed to fetch monthly collection data');
      }
    } catch (error) {
      toast.error('Error fetching monthly collection data. Please try again.');
    }
  };

  const billedUnbilledData = async () => {
    try {
      const response = await axiosInstance.post('AccDashboard/AccDashboard?currentPage=1&limit=15', {
        PropId: globalProperty,
        FromDt: dateFrom.format('YYYY-MM-DD'),
        ToDt: dateTo.format('YYYY-MM-DD'),
        Flag: "TotalBillAmount",
        start: 1,
        PageSize: 10,
        SearchText: ""
      });

      if (response.data.Status === 0) {
        const data = response.data.Data;

        const formattedData = data.map((item) => ({
          name: item.Name || "",
          amount: item.TotalCount || 0,
        }));

        setBillUnbillData(formattedData);
      } else {
        setBillUnbillData([]);
        // toast.info('Failed to fetch billed/unbilled data');
      }
    } catch (error) {
      toast.error("Error while fetching billed/unbilled data.");
    }
  };

  const billed = billunbilledData.find(item => item.name.toLowerCase().includes("billed"))?.amount || 0;
  const unbilled = billunbilledData.find(item => item.name.toLowerCase().includes("unbilled"))?.amount || 0;

  const total = billed + unbilled;
  const billedPercent = total > 0 ? (billed / total) * 100 : 0;
  const unbilledPercent = total > 0 ? (unbilled / total) * 100 : 0;

  // Category Wise Revenue
  const categoryWiseRevenue = async () => {
    try {
      const response = await axiosInstance.post('AccDashboard/AccDashboard?currentPage=1&limit=15', {
        PropId: globalProperty,
        FromDt: dateFrom.format('YYYY-MM-DD'),
        ToDt: dateTo.format('YYYY-MM-DD'),
        Flag: "TotalCategoryWise",
        start: 1,
        PageSize: 10,
        SearchText: ""
      });

      if (response.data.Status === 0) {
        setCategoryData(response.data.Data);
      } else {
        setCategoryData([]);
        // toast.info(response.data.Message);
      }
    } catch (error) {
      toast.error('Error while fetching record.');
    }
  };

  // Received Amount
  const receivedAmntType = async () => {
    try {
      const response = await axiosInstance.post('AccDashboard/AccDashboard?currentPage=1&limit=15', {
        PropId: globalProperty,
        FromDt: dateFrom.format('YYYY-MM-DD'),
        ToDt: dateTo.format('YYYY-MM-DD'),
        Flag: "Onaccount",
        start: 1,
        PageSize: 10,
        SearchText: ""
      });
      if (response.data.Status === 0) {
        const rawData = response.data.Data[0];
        const formattedData = [
          { label: 'Total Received', value: rawData?.TotalAmt || 0 },
          { label: 'Bill Wise', value: rawData?.Amount || 0 },
          { label: 'On Account', value: rawData?.TotalSum || 0 }
        ]
        setReceivedAmnt(formattedData);
      } else {
        setReceivedAmnt([]);
        // toast.info(response.data.Message);
      }
    } catch (error) {
      toast.error('Error while fetching record');
    }
  };

  const handleCardDoubleClick = (cardTitle) => {
    let hasPermission = false;
    let url = '';
    if (cardTitle === 'Total Revenue') {
      hasPermission = Permissions?.ViewRec === "1";
      url = '/advicegeneratetable';
    } else if (cardTitle === 'Total Collection') {
      hasPermission = reciptPermission?.ViewRec === "1";
      url = '/receipt-table';
    } else if (cardTitle === 'Total Dues') {
      hasPermission = Permissions?.ViewRec === "1";
      url = '/advicegeneratetable';
    } else if (cardTitle === 'Total Credit Note') {
      hasPermission = CreditPermissions?.ViewRec === "1";
      url = '/cradit-notestable';
    }
    if (hasPermission) {
      window.open(url, '_blank');
    } else {
      toast.warning(`You don't have permission to view ${cardTitle}`);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 2 }, bgcolor: '#f5f7fa', minHeight: '100vh' }}>
      <ToastContainer />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => navigate('/masters/company')} size="small">
            <ArrowBackIcon sx={{ color: '#635bff' }} />
          </IconButton>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 'bold',
              color: '#635bff',
              fontSize: { xs: '1.5rem', sm: '2rem' },
              userSelect: 'none'
            }}
          >
            Account Dashboard
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* <CalendarPage onDateChange={handleDateChange} /> */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <DatePicker
                label="From-Date"
                value={dateFrom}
                onChange={(newValue) => setDateFrom(newValue)}
                format="DD/MM/YYYY"
                views={['day', 'month', 'year']}
                sx={{
                  width: 150,
                  '& .MuiInputBase-input': {
                    padding: '7px 10px !important',
                  },
                }}
              />
              <DatePicker
                label="To-Date"
                value={dateTo}
                onChange={(newValue) => setDateTo(newValue)}
                format="DD/MM/YYYY"
                views={['day', 'month', 'year']}
                sx={{
                  width: 150,
                  '& .MuiInputBase-input': {
                    padding: '7px 10px !important',
                  },
                }}
              />
            </Box>
          </LocalizationProvider>
          <Tooltip title='Click to retreive data' arrow>
            <Button
              variant='contained'
              onClick={handleFetchedData}
              sx={{ borderRadius: '8px', backgroundColor: '#635bff' }}
            >
              Get Data
            </Button>
          </Tooltip>
        </Box>
      </Box>

      <Grid container spacing={2}>
        {[
          { title: 'Total Revenue', amount: totalRevenue, color: '#2196f3', icon: <TrendingUpIcon />, permission: Permissions?.ViewRec === "1" },
          { title: 'Total Collection', amount: totalCollection, color: '#4caf50', icon: <TrendingDownIcon />, permission: reciptPermission?.ViewRec === "1" },
          { title: 'Total Dues', amount: totalDues, color: '#f44336', icon: <AccountBalanceWalletIcon />, permission: Permissions?.ViewRec === "1" },
          { title: 'Total Credit Note', amount: totalCreditNote, color: '#e91e63', icon: <CreditCardIcon />, permission: CreditPermissions?.ViewRec === "1" },
        ].map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <StyledCard sx={{ background: `linear-gradient(135deg, ${card.color} 0%, ${card.color}99 100%)` }}
              onDoubleClick={() => handleCardDoubleClick(card.title)}
            >
              <Box sx={{ mr: 2 }}>{card.icon}</Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {card.title}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {formatCurrency(card.amount)}
                </Typography>
              </Box>
            </StyledCard>
          </Grid>
        ))}
      </Grid>

      <Grid container rowSpacing={1.5} columnSpacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} md={4}>
          <StyledPaper>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#1a1a1a' }}>
              Quarter Wise Revenue
            </Typography>
            <PieChart
              series={[
                {
                  data: quarterlyRevenueData.length > 0 ? quarterlyRevenueData : [
                    { id: 0, value: 0, label: 'No Data', color: '#8884d8' }
                  ],
                  innerRadius: 30,
                  outerRadius: 100,
                  paddingAngle: 2,
                  cornerRadius: 5,
                },
              ]}
              width={420}
              height={200}
              legend={{
                hidden: false,
                position: {
                  vertical: 'middle',
                  horizontal: 'right',
                },
                padding: 10,
              }}
              slotProps={{
                legend: {
                  labelStyle: { fontSize: 12 },
                  itemMarkWidth: 22,
                  itemGap: 6,
                },
              }}
              sx={{ ml: -15 }}
            />
            <Typography variant="body2" sx={{ mt: 2 }}>
              Sum of Quarterly Revenue: ₹
              {quarterlyRevenueData
                .reduce((acc, curr) => acc + (curr.value || 0), 0)
                .toLocaleString('en-IN')}
            </Typography>
            <Button variant="text">View Report Quarterly Revenue</Button>
          </StyledPaper>
        </Grid>

        <Grid item xs={12} md={4}>
          <StyledPaper sx={{ py: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a1a1a' }}>
              Month Wise Collections
            </Typography>
            <BarChart
              dataset={mnthlyCollData}
              yAxis={[{ scaleType: 'band', dataKey: 'period', tickPlacement: 'middle' }]}
              margin={{ left: 70, right: 100, top: 20, bottom: 20 }}
              // xAxis={[{
              //   valueFormatter: (value) => (value / 1000000).toFixed(0),
              // }]}
              xAxis={[
                {
                  valueFormatter: (value) => {
                    if (value >= 10000000) return `${(value / 10000000).toFixed(1)}Cr`;
                    if (value >= 100000) return `${(value / 100000).toFixed(1)}L`;
                    if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
                    return value.toString();
                  },
                },
              ]}
              series={[
                {
                  dataKey: 'count',
                  label: 'Collections',
                  color: '#2196f3',
                },
              ]}
              width={400}
              height={245}
              layout="horizontal"
              // barLabel="value"
              slotProps={{
                legend: {
                  position: {
                    vertical: 'top',
                    horizontal: 'right',
                  },
                },
              }}
              sx={{
                mt: -2,
                '& .MuiBarElement-label': {
                  fill: '#ffffff',
                  fontWeight: 'bold',
                  fontSize: 12,
                },
              }}
            />
            <Typography variant="body2" sx={{ mt: 0 }}>
              Sum of monthly collection: ₹
              {mnthlyCollData
                .reduce((sum, item) => sum + Number(item.count || 0), 0)
                .toLocaleString('en-IN')}
            </Typography>
            <Button variant="text">
              View Report Billings with Billing Lines
            </Button>
          </StyledPaper>
        </Grid>

        <Grid item xs={12} md={4}>
          <StyledPaper>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#1a1a1a' }}>
              Billed & Unbilled Summary
            </Typography>

            {/* Billed Section */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Tooltip title='Generated Payment Advice' arrow>
                <Box display="flex" alignItems="center" gap={1}>
                  <CheckCircleIcon color="success" />
                  <Typography variant="subtitle1" fontWeight={600}>Billed</Typography>
                  <Chip label="Completed" size="small" color="success" />
                </Box>
              </Tooltip>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography variant="h6" color="success.main">₹{billed.toLocaleString('en-IN')}</Typography>
                <ArrowUpwardIcon sx={{ color: '#4caf50' }} fontSize="small" />
                <Typography variant="body2" color="success.main" fontWeight={600}>
                  {billedPercent.toFixed(0)}%
                </Typography>
              </Box>
            </Box>
            <LinearProgress
              variant="determinate"
              value={billedPercent}
              sx={{ height: 10, borderRadius: 5, bgcolor: '#e0f2f1' }}
              color="success"
            />
            <Typography variant="body2" mt={0.5} mb={2} color="text.secondary">
              {billedPercent.toFixed(0)}% of total
            </Typography>

            {/* Unbilled Section */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Tooltip title='Pending Payment Advice' arrow>
                <Box display="flex" alignItems="center" gap={1}>
                  <AccessTimeIcon color="warning" />
                  <Typography variant="subtitle1" fontWeight={600}>Unbilled</Typography>
                  <Chip label="Pending" size="small" color="warning" />
                </Box>
              </Tooltip>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography variant="h6" color="warning.main">₹{unbilled.toLocaleString('en-IN')}</Typography>
                <ArrowDownwardIcon sx={{ color: '#f44336' }} fontSize="small" />
                <Typography variant="body2" color="error.main" fontWeight={600}>
                  {unbilledPercent.toFixed(0)}%
                </Typography>
              </Box>
            </Box>
            <LinearProgress
              variant="determinate"
              value={unbilledPercent}
              sx={{ height: 10, borderRadius: 5, bgcolor: '#fff3e0' }}
              color="warning"
            />
            <Typography variant="body2" mt={0.5} color="text.secondary">
              {unbilledPercent.toFixed(0)}% of total
            </Typography>

            <Button variant="text" sx={{ mt: 8 }}>
              View Detailed Report
            </Button>
          </StyledPaper>
        </Grid>

        <Grid item xs={12} md={4}>
          <StyledPaper>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#1a1a1a' }}>
              Category Wise Revenue
            </Typography>

            <BarChart
              width={400}
              height={250}
              margin={{ left: 70, right: 100, top: 20, bottom: 20 }}
              xAxis={[
                {
                  data: categoryData.map((item) => item.Name || 'Unknown'),
                  scaleType: 'band',
                  label: '',
                  tickPlacement: 'middle',
                },
              ]}
              series={[
                {
                  data: categoryData.map((item) => item.TotalCount || 0),
                  label: 'Revenue',
                  color: 'rgb(255 64 0)',
                },
              ]}
              yAxis={[
                {
                  label: '',
                  tickPlacement: 'middle',
                },
              ]}
              legend={{
                position: {
                  vertical: 'top',
                  horizontal: 'right',
                },
                direction: 'column',
                padding: 10,
              }}
              sx={{
                '.MuiBarElement-root': {
                  borderRadius: 4,
                },
              }}
            />
            <Typography variant="body2" sx={{ mt: 2 }}>
              Category Total : {formatCurrency(categoryData.reduce((sum, item) => sum + (item.TotalCount || 0), 0))}
            </Typography>
            <Button variant="text">View Report Net Profit Transactions</Button>
          </StyledPaper>
        </Grid>

        <Grid item xs={12} md={4}>
          <StyledPaper>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#1a1a1a' }}>
              Received Amount
            </Typography>
            <Box sx={{ width: 400, paddingRight: 50 }}>
              <PieChart
                series={[
                  {
                    data: receivedAmnt.map((item, index) => ({
                      id: index + 1,
                      value: item.value,
                      label: item.label,
                    })),
                    innerRadius: 30,
                    outerRadius: 100,
                    paddingAngle: 2,
                    cornerRadius: 5,
                  },
                ]}
                width={400} // increased width from 350
                height={200}
                legend={{
                  hidden: false,
                  position: { vertical: 'center', horizontal: 'right' },
                  padding: 10,
                }}
                sx={{ ml: -8 }} // shift toward left
                slotProps={{ legend: { labelStyle: { fontSize: 12 } } }}
              />
            </Box>

            <Box sx={{ mt: 3, textAlign: 'right' }}>
              {receivedAmnt.map((item) => (
                <Typography key={item.label} variant="body2">
                  {item.label}: {formatCurrency(item.value)}
                </Typography>
              ))}
            </Box>
            <Button variant="text">View Report Historical AR Dashboard</Button>
          </StyledPaper>
        </Grid>

        <Grid item xs={12} md={4}>
          <StyledPaper sx={{ pt: 1, p: 1, pb: 2 }}>
            <Typography variant="h6" sx={{ mt: 0, mb: 1, fontWeight: 'bold', color: '#1a1a1a' }}>
              Recent Transaction
            </Typography>
            <AccountingTable
              PropId={globalProperty}
              FromDt={dateFrom.format('YYYY-MM-DD')}
              ToDt={dateTo.format('YYYY-MM-DD')}
            />
          </StyledPaper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AccountingDash;